import React, { useState } from 'react';
import { CheckSquare, Square, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

const WhatIfChecklist = ({ worksheet }) => {
  const [expandedCategories, setExpandedCategories] = useState(['flammability']);
  const [checkedItems, setCheckedItems] = useState({});

  const whatIfQuestions = {
    flammability: [
      'What if flammable vapors accumulate in the work area?',
      'What if ignition sources are present during maintenance?',
      'What if flammable materials are not properly removed before hot work?',
      'What if static electricity causes ignition?',
      'What if LEL levels exceed safe limits?'
    ],
    toxicity: [
      'What if toxic gases are released during equipment opening?',
      'What if personnel exposure exceeds PEL/TLV limits?',
      'What if ventilation system fails during chemical handling?',
      'What if emergency response equipment is not available?',
      'What if toxic residues remain after cleaning?'
    ],
    energySources: [
      'What if electrical equipment is not properly de-energized?',
      'What if stored energy (pressure, spring, gravity) is released?',
      'What if lockout/tagout procedures are not followed?',
      'What if backup power systems activate unexpectedly?',
      'What if residual energy causes equipment movement?'
    ],
    mechanical: [
      'What if rotating equipment starts unexpectedly?',
      'What if guards or barriers are removed during maintenance?',
      'What if pinch points are not identified?',
      'What if mechanical integrity is compromised?',
      'What if equipment fails under load?'
    ],
    pressure: [
      'What if system pressure is not fully relieved?',
      'What if pressure build-up occurs during maintenance?',
      'What if pressure relief devices are blocked?',
      'What if over-pressurization occurs?',
      'What if pressure testing causes equipment failure?'
    ],
    temperature: [
      'What if equipment surface temperatures exceed safe limits?',
      'What if cryogenic materials cause cold burns?',
      'What if cooling system fails during operation?',
      'What if thermal expansion causes equipment damage?',
      'What if hot work generates excessive heat?'
    ]
  };

  const categoryLabels = {
    flammability: 'Flammability Hazards',
    toxicity: 'Toxicity Hazards',
    energySources: 'Energy Sources',
    mechanical: 'Mechanical Hazards',
    pressure: 'Pressure Hazards',
    temperature: 'Temperature Hazards'
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev?.includes(category)
        ? prev?.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setCheckedItems(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const getCategoryCompletion = (category) => {
    const questions = whatIfQuestions?.[category];
    const checkedCount = questions?.filter((_, index) =>
      checkedItems?.[`${category}-${index}`]
    )?.length;
    return {
      completed: checkedCount,
      total: questions?.length,
      percentage: Math.round((checkedCount / questions?.length) * 100)
    };
  };

  if (!worksheet) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Select a worksheet to begin what-if analysis</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">What-If Checklist</h2>
        <p className="text-sm text-gray-600">
          Systematic deviation analysis for {worksheet?.title}
        </p>
      </div>
      {/* Checklist Categories */}
      <div className="space-y-4">
        {Object.entries(whatIfQuestions)?.map(([category, questions]) => {
          const completion = getCategoryCompletion(category);
          const isExpanded = expandedCategories?.includes(category);

          return (
            <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  )}
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {categoryLabels?.[category]}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {completion?.completed} of {completion?.total} questions completed
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      {completion?.percentage}%
                    </div>
                  </div>
                  <div className="w-32 bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all"
                      style={{ width: `${completion?.percentage}%` }}
                    />
                  </div>
                </div>
              </button>
              {/* Questions List */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-6">
                  <div className="space-y-3">
                    {questions?.map((question, index) => {
                      const isChecked = checkedItems?.[`${category}-${index}`];

                      return (
                        <button
                          key={index}
                          onClick={() => toggleItem(category, index)}
                          className="w-full flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                        >
                          <div className="mt-0.5">
                            {isChecked ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <span className={`flex-1 ${isChecked ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                            {question}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Checklist Summary</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-3xl font-bold text-gray-900">
              {Object.values(checkedItems)?.filter(Boolean)?.length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Questions Completed</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-gray-900">
              {Object.values(whatIfQuestions)?.reduce((sum, arr) => sum + arr?.length, 0)}
            </div>
            <div className="text-sm text-gray-600 mt-1">Total Questions</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-600">
              {Math.round(
                (Object.values(checkedItems)?.filter(Boolean)?.length /
                  Object.values(whatIfQuestions)?.reduce((sum, arr) => sum + arr?.length, 0)) *
                  100
              )}%
            </div>
            <div className="text-sm text-gray-600 mt-1">Overall Completion</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatIfChecklist;